import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Package,
  Truck,
  CheckCircle2,
  Printer,
  Book,
  Image as ImageIcon,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '@/lib/api';

// Fulfillment lifecycle, in order:
//   awaiting_payment → submitting_to_gelato → submitted → printing → shipped → delivered
const STAGES = [
  { key: 'paid',      label: 'Paid',      icon: CheckCircle2 },
  { key: 'submitted', label: 'Submitted', icon: Package },
  { key: 'printing',  label: 'Printing',  icon: Printer },
  { key: 'shipped',   label: 'Shipped',   icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2 },
];

// Map Gelato's fulfillment_status strings to one of our 5 stages
function stageIndexFor(status) {
  const s = (status || '').toLowerCase();
  if (s.includes('deliver')) return 4;
  if (s.includes('ship') || s === 'shipped' || s.includes('transit')) return 3;
  if (s.includes('print') || s === 'in_production' || s.includes('production')) return 2;
  if (s.includes('submit') || s.includes('created') || s === 'pending' || s.includes('passed')) return 1;
  if (s === 'awaiting_payment') return -1;
  return 0;  // paid, no further info yet
}

function isFailed(o) {
  return o.fulfillment_status === 'gelato_failed' || o.fulfillment_status === 'gelato_billing_required';
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'MMM d, yyyy'); } catch { return iso; }
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return `$${Number(n).toFixed(2)}`;
}

function StatusTrack({ order }) {
  if (isFailed(order)) {
    return (
      <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="track-failed">
        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900">
          We hit a snag printing this order — we&apos;re on it. You won&apos;t be charged again, and we&apos;ll
          email you when it&apos;s on the way.
        </div>
      </div>
    );
  }
  const idx = stageIndexFor(order.fulfillment_status);
  return (
    <div className="mt-3" data-testid="track-stages">
      <div className="flex items-center gap-1">
        {STAGES.map((s, i) => {
          const Done = i <= idx;
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex-1 flex items-center gap-1 min-w-0">
              <div className={`flex flex-col items-center gap-1 ${i === 0 ? '' : 'flex-1'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition ${
                  Done ? 'bg-teal-600 text-white' : 'bg-stone-100 text-stone-400'
                }`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className={`text-[10px] font-medium leading-none text-center ${Done ? 'text-teal-700' : 'text-stone-400'}`}>
                  {s.label}
                </span>
              </div>
              {i < STAGES.length - 1 && (
                <div className={`h-0.5 flex-1 -mt-4 ${i < idx ? 'bg-teal-600' : 'bg-stone-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderCard({ order, onSync, syncing, onReorder }) {
  const isPhotobook = order.order_type === 'photobook';
  const PreviewIcon = isPhotobook ? Book : ImageIcon;
  const placedDate = order.paid_at || order.submitted_at || order.created_at;
  const failed = isFailed(order);
  const shipped = !failed && stageIndexFor(order.fulfillment_status) >= 3;

  return (
    <Card className="p-4 rounded-2xl border-stone-200 bg-white" data-testid={`order-${order.id}`}>
      <div className="flex items-start gap-3">
        {/* Preview thumbnail */}
        <div className="w-16 h-16 rounded-xl bg-teal-50 flex items-center justify-center shrink-0 overflow-hidden">
          {order.order_type === 'photo_print' && order.file_id ? (
            <img
              src={`${API_BASE}/files/${order.file_id}`}
              alt={order.product_title}
              className="w-full h-full object-cover"
            />
          ) : (
            <PreviewIcon className="w-7 h-7 text-teal-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-stone-900 truncate">{order.product_title || 'Print order'}</div>
              <div className="text-xs text-stone-500 mt-0.5">
                Ordered {fmtDate(placedDate)} · {order.quantity} × {fmtMoney(order.retail_price_usd)}
                {order.shipping_city && (
                  <> · ships to {order.shipping_city}{order.shipping_state ? `, ${order.shipping_state}` : ''}</>
                )}
              </div>
              {!order.is_mine && order.customer_name && (
                <div className="text-[11px] text-stone-400 mt-0.5">Ordered by {order.customer_name}</div>
              )}
            </div>
            <div className="text-sm font-semibold text-teal-700 shrink-0">{fmtMoney(order.amount_usd)}</div>
          </div>
        </div>
      </div>

      <StatusTrack order={order} />

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {order.tracking_url ? (
          <a
            href={order.tracking_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-teal-700 hover:underline inline-flex items-center gap-1"
            data-testid={`tracking-${order.id}`}
          >
            <Truck className="w-3.5 h-3.5" />
            Track package
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : shipped ? (
          <span className="text-xs text-stone-500 inline-flex items-center gap-1">
            <Truck className="w-3.5 h-3.5" />
            Tracking arrives soon
          </span>
        ) : null}

        <button
          type="button"
          onClick={() => onSync(order.id)}
          disabled={syncing === order.id}
          className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 ml-auto disabled:opacity-60"
          data-testid={`sync-${order.id}`}
        >
          {syncing === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh status
        </button>

        {order.order_type === 'photo_print' && order.file_id && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReorder(order)}
            className="rounded-full text-xs h-7 px-3 border-teal-200 text-teal-700 hover:bg-teal-50"
            data-testid={`reorder-${order.id}`}
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Reorder
          </Button>
        )}
      </div>
    </Card>
  );
}

export default function OrdersPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justPaidId = searchParams.get('just_paid');
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('mine');  // 'mine' | 'family'
  const [fetching, setFetching] = useState(true);
  const [syncing, setSyncing] = useState(null);

  const load = useCallback(async (which) => {
    setFetching(true);
    try {
      const url = `${API_BASE}/prints/orders${which === 'mine' ? '?mine_only=true' : ''}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setOrders(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load orders');
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    load(tab);
  }, [user, loading, tab, navigate, load]);

  const sync = async (id) => {
    setSyncing(id);
    try {
      await axios.post(`${API_BASE}/prints/orders/${id}/sync-status`, {}, { withCredentials: true });
      toast.success('Status refreshed');
      load(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not refresh');
    } finally {
      setSyncing(null);
    }
  };

  const reorder = (order) => {
    // For photo prints, deep-link back to the album with the file pre-selected.
    // Photobooks need their album context, so just hint to the user.
    if (order.order_type === 'photo_print' && order.album_id) {
      navigate(`/albums/${order.album_id}?reorder=${order.file_id}`);
    } else if (order.album_id) {
      navigate(`/albums/${order.album_id}`);
    } else {
      navigate('/albums');
      toast('Pick a photo or album to order again');
    }
  };

  if (loading || (fetching && orders.length === 0)) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="orders-page">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-8">
        {justPaidId && (
          <Card className="p-4 rounded-2xl mb-5 bg-teal-50 border-teal-200" data-testid="just-paid-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-teal-900">Thanks — your order is in! 🎉</div>
                <div className="text-sm text-teal-800 mt-0.5">
                  We&apos;ll print &amp; ship it within a few days. You&apos;ll get a tracking link as soon as it leaves the press.
                </div>
              </div>
            </div>
          </Card>
        )}

        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-stone-900">Print orders</h1>
            <p className="text-sm text-stone-500 mt-1">Track your photo books &amp; prints from order to mailbox.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTab('mine')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                tab === 'mine' ? 'bg-teal-600 text-white' : 'bg-white text-stone-600 border border-stone-200'
              }`}
              data-testid="tab-mine"
            >
              Mine
            </button>
            <button
              onClick={() => setTab('family')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                tab === 'family' ? 'bg-teal-600 text-white' : 'bg-white text-stone-600 border border-stone-200'
              }`}
              data-testid="tab-family"
            >
              Family
            </button>
          </div>
        </div>

        {orders.length === 0 ? (
          <Card className="p-10 text-center rounded-2xl border-stone-200 bg-white" data-testid="empty-state">
            <Package className="w-12 h-12 text-teal-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-stone-900 mb-1">No orders yet</h3>
            <p className="text-sm text-stone-500 mb-5">
              Turn your favorite memories into prints &amp; photo books that arrive in the mail.
            </p>
            <Button
              onClick={() => navigate('/albums')}
              className="rounded-full bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="browse-albums"
            >
              Browse albums
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                onSync={sync}
                syncing={syncing}
                onReorder={reorder}
              />
            ))}
          </div>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
